import {
	Box,
	Chip,
	IconButton,
	TextField,
	Autocomplete,
	Button,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	List,
	ListItemButton,
	ListItemText,
	ListItemAvatar,
	Avatar,
} from "@mui/material";
import { Visibility, CheckCircle, Reply, Settings, Add, Map } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import MDButton from "../../components/MDButton";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";

const STATUS_COLORS = {
	APERTA: "#ff9800",
	RISPOSTA: "#2196f3",
	APPROVATA: "#4caf50",
};

const SnagListTable = () => {
	const navigate = useNavigate();
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [areas, setAreas] = useState([]);
	const [floorPlans, setFloorPlans] = useState([]);
	const [floorPlanDialogOpen, setFloorPlanDialogOpen] = useState(false);
	const storedAreaId = localStorage.getItem("areaSelected");
	const [filters, setFilters] = useState({
		area_id: storedAreaId ? parseInt(storedAreaId) : null,
		floor_plan_id: null,
		status: null,
	});

	const stati = [
		{ value: "APERTA", label: "Aperta" },
		{ value: "RISPOSTA", label: "Risposta" },
		{ value: "APPROVATA", label: "Approvata" },
	];

	useEffect(() => {
		fetchAreas();
	}, []);

	useEffect(() => {
		fetchSnagItems();
	}, [filters]);

	useEffect(() => {
		if (filters.area_id) {
			fetchFloorPlans(filters.area_id);
		} else {
			setFloorPlans([]);
			setFilters((prev) => ({ ...prev, floor_plan_id: null }));
		}
	}, [filters.area_id]);

	const fetchAreas = async () => {
		try {
			const response = await apiService.get("/area-management/", {}, {});
			if (response && Array.isArray(response)) {
				setAreas(response.filter((area) => area.yard === true));
			}
		} catch (error) {
			toast.error("Errore caricamento cantieri");
		}
	};

	const fetchFloorPlans = async (areaId) => {
		try {
			const response = await apiService.get("/snaglist/floor-plans/", { area_id: areaId }, {});
			setFloorPlans(response || []);
		} catch (error) {
			setFloorPlans([]);
		}
	};

	const fetchSnagItems = async () => {
		setLoading(true);
		try {
			const params = {};
			if (filters.area_id) params.area_id = filters.area_id;
			if (filters.floor_plan_id) params.floor_plan_id = filters.floor_plan_id;
			if (filters.status) params.status = filters.status;

			const response = await apiService.get("/snaglist/snag-items/", params, {});
			setData(response || []);
		} catch (error) {
			toast.error("Errore caricamento snag list");
			setData([]);
		} finally {
			setLoading(false);
		}
	};

	const handleRespond = async (snagId) => {
		try {
			await apiService.post(`/snaglist/snag-items/${snagId}/respond/`, {}, {});
			toast.success("Snag marcato come risposto");
			fetchSnagItems();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleApprove = async (snagId) => {
		try {
			await apiService.post(`/snaglist/snag-items/${snagId}/approve/`, {}, {});
			toast.success("Snag approvato");
			fetchSnagItems();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleCreateSnag = () => {
		if (!filters.area_id) {
			toast.error("Seleziona un cantiere prima di creare una segnalazione");
			return;
		}
		if (floorPlans.length === 0) {
			toast.error("Nessuna planimetria disponibile per questo cantiere. Caricane una da Gestisci Planimetrie.");
			return;
		}
		if (floorPlans.length === 1) {
			navigate(`/snag-list/floor-plan/${filters.area_id}/${floorPlans[0].id}`);
			return;
		}
		setFloorPlanDialogOpen(true);
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: "work_package_display",
				header: "Pacchetto di lavoro",
				size: 160,
			},
			{
				accessorKey: "code",
				header: "Compito",
				size: 100,
			},
			{
				id: "image",
				header: "Immagine",
				size: 80,
				Cell: ({ row }) =>
					row.original.first_image_url ? (
						<img
							src={row.original.first_image_url}
							alt="snag"
							style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
						/>
					) : (
						<Typography variant="caption" color="textSecondary">
							-
						</Typography>
					),
			},
			{
				id: "position",
				header: "Posizione",
				size: 80,
				Cell: ({ row }) =>
					row.original.floor_plan_image_url ? (
						<Box
							sx={{
								position: "relative",
								width: 60,
								height: 60,
								borderRadius: 1,
								overflow: "hidden",
								border: "1px solid #ddd",
							}}
						>
							<img
								src={row.original.floor_plan_image_url}
								alt="floor plan"
								style={{ width: "100%", height: "100%", objectFit: "cover" }}
							/>
							<Box
								sx={{
									position: "absolute",
									left: `${row.original.x_percent}%`,
									top: `${row.original.y_percent}%`,
									transform: "translate(-50%, -50%)",
									width: 8,
									height: 8,
									borderRadius: "50%",
									bgcolor: STATUS_COLORS[row.original.status] || "#ff9800",
									border: "1px solid white",
								}}
							/>
						</Box>
					) : (
						"-"
					),
			},
			{
				id: "location_desc",
				header: "Descrizione posizione",
				size: 180,
				Cell: ({ row }) => (
					<Box>
						<Typography variant="body2">{row.original.area_name}</Typography>
						<Typography variant="caption" color="textSecondary">
							{row.original.floor_plan_name}
						</Typography>
					</Box>
				),
			},
			{
				accessorKey: "description",
				header: "Descrizione",
				size: 200,
				Cell: ({ cell }) => (
					<span title={cell.getValue()}>
						{cell.getValue() && cell.getValue().length > 50
							? cell.getValue().substring(0, 50) + "..."
							: cell.getValue()}
					</span>
				),
			},
			{
				accessorKey: "created_by_name",
				header: "Creato da",
				size: 130,
			},
			{
				accessorKey: "responded_by_name",
				header: "Risposto da",
				size: 130,
			},
			{
				accessorKey: "created_at",
				header: "Data creata",
				size: 120,
				Cell: ({ cell }) =>
					cell.getValue() ? new Date(cell.getValue()).toLocaleDateString("it-IT") : "-",
			},
			{
				accessorKey: "responded_at",
				header: "Data risposta",
				size: 120,
				Cell: ({ cell }) =>
					cell.getValue() ? new Date(cell.getValue()).toLocaleDateString("it-IT") : "-",
			},
			{
				accessorKey: "status_display",
				header: "Stato",
				size: 120,
				Cell: ({ cell, row }) => (
					<Chip
						label={cell.getValue()}
						size="small"
						sx={{
							bgcolor: STATUS_COLORS[row.original.status],
							color: "white",
						}}
					/>
				),
			},
		],
		[]
	);

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box p={3}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Typography variant="h4" color="primary">
						Snag List
					</Typography>
					<Box display="flex" gap={1}>
						<MDButton
							variant="contained"
							color="success"
							startIcon={<Add />}
							onClick={handleCreateSnag}
						>
							Crea segnalazione
						</MDButton>
						<MDButton
							variant="outlined"
							color="info"
							startIcon={<Settings />}
							onClick={() => navigate("/snag-list/floor-plans")}
						>
							Gestisci Planimetrie
						</MDButton>
					</Box>
				</Box>

				{/* Filters */}
				<Box display="flex" gap={2} mb={3} flexWrap="wrap">
					<Autocomplete
						options={areas}
						getOptionLabel={(option) => option.name || ""}
						value={areas.find((a) => a.id === filters.area_id) || null}
						onChange={(event, newValue) => {
							setFilters((prev) => ({
								...prev,
								area_id: newValue ? newValue.id : null,
								floor_plan_id: null,
							}));
						}}
						renderInput={(params) => <TextField {...params} label="Cantiere" />}
						sx={{ minWidth: 250 }}
					/>
					<Autocomplete
						options={floorPlans}
						getOptionLabel={(option) => option.name || ""}
						value={floorPlans.find((fp) => fp.id === filters.floor_plan_id) || null}
						onChange={(event, newValue) => {
							setFilters((prev) => ({ ...prev, floor_plan_id: newValue ? newValue.id : null }));
						}}
						renderInput={(params) => <TextField {...params} label="Planimetria" />}
						sx={{ minWidth: 200 }}
						disabled={!filters.area_id}
					/>
					<Autocomplete
						options={stati}
						getOptionLabel={(option) => option.label || ""}
						value={stati.find((s) => s.value === filters.status) || null}
						onChange={(event, newValue) => {
							setFilters((prev) => ({ ...prev, status: newValue ? newValue.value : null }));
						}}
						renderInput={(params) => <TextField {...params} label="Stato" />}
						sx={{ minWidth: 150 }}
					/>
					<Button
						variant="outlined"
						onClick={() => setFilters({ area_id: null, floor_plan_id: null, status: null })}
					>
						Reset Filtri
					</Button>
				</Box>

				{/* Table */}
				<MaterialReactTable
					columns={columns}
					data={data}
					enableRowActions
					positionActionsColumn="last"
					renderRowActions={({ row }) => (
						<Box display="flex" gap={0.5}>
							<IconButton
								size="small"
								onClick={() =>
									navigate(
										`/snag-list/floor-plan/${row.original.area_id}/${row.original.floor_plan_id}`
									)
								}
								title="Visualizza su planimetria"
							>
								<Visibility />
							</IconButton>
							{row.original.status === "APERTA" && (
								<IconButton
									size="small"
									onClick={() => handleRespond(row.original.id)}
									title="Rispondi"
									color="primary"
								>
									<Reply />
								</IconButton>
							)}
							{row.original.status === "RISPOSTA" && (
								<IconButton
									size="small"
									onClick={() => handleApprove(row.original.id)}
									title="Approva"
									color="success"
								>
									<CheckCircle />
								</IconButton>
							)}
						</Box>
					)}
					state={{ isLoading: loading }}
					enableSorting
					enableColumnFilters={false}
					enablePagination
					initialState={{ pagination: { pageSize: 10 } }}
				/>
			</Box>

			{/* Floor plan picker dialog */}
			<Dialog
				open={floorPlanDialogOpen}
				onClose={() => setFloorPlanDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Seleziona planimetria</DialogTitle>
				<DialogContent>
					<List>
						{floorPlans.map((fp) => (
							<ListItemButton
								key={fp.id}
								onClick={() => {
									setFloorPlanDialogOpen(false);
									navigate(`/snag-list/floor-plan/${filters.area_id}/${fp.id}`);
								}}
							>
								<ListItemAvatar>
									{fp.image_url ? (
										<Avatar variant="rounded" src={fp.image_url} />
									) : (
										<Avatar variant="rounded">
											<Map />
										</Avatar>
									)}
								</ListItemAvatar>
								<ListItemText
									primary={fp.name}
									secondary={`${fp.snag_count || 0} snag`}
								/>
							</ListItemButton>
						))}
					</List>
				</DialogContent>
			</Dialog>
		</DashboardLayout>
	);
};

export default SnagListTable;
